import {FaTrophy} from "react-icons/fa";
import {TournamentBracket, BracketRound as BracketRoundType, TournamentTeam} from "@/competitions/tournaments/types";
import BracketRound, {BracketRoundProps} from "./BracketRound";
import BracketCanvas from "./BracketCanvas";

interface GroupsKnockoutViewProps {
    bracket: TournamentBracket;
    teamNames?: Record<string, string>;
    championName: string | null;
    /**
     * Shared props forwarded to each knockout BracketRound.
     *
     * Derived from BracketRoundProps rather than re-declared: the hand-written
     * copy that used to live here omitted `tournamentId`, so the spread below
     * did not typecheck against the component it spreads into. Deriving it means
     * anything added to BracketRound arrives here automatically.
     */
    roundProps: Omit<BracketRoundProps, "round" | "depth" | "isFinal" | "labelOffset">;
}

type PhaseState = "done" | "live" | "pending";

/**
 * GROUPS_KNOCKOUT (GSL-style) bracket view per the mockup's groups format:
 * numbered phase stepper (Group Stage → Playoffs → Grand Final), per-group
 * standings cards (green qualification bar on advancing rows, cyan YOU row,
 * W–L + differential), then the knockout bracket in the pan/zoom canvas.
 */
export default function GroupsKnockoutView({bracket, teamNames, championName, roundProps}: GroupsKnockoutViewProps) {
    const groups = bracket.groups ?? [];
    const knockoutRounds: BracketRoundType[] = bracket.rounds ?? [];
    const groupStageComplete = groups.length > 0 && groups.every((g) => g.isComplete);
    const highlightTeamId = roundProps.highlightTeamId;

    const name = (id: string) => roundProps.teams[id]?.name || teamNames?.[id] || "TBD";

    // Nothing generated yet (no group pools and no knockout rounds).
    if (groups.length === 0 && knockoutRounds.length === 0) {
        return (
            <div className="bracket-viewer bracket-viewer--empty">
                <p>Bracket not yet generated</p>
            </div>
        );
    }

    /*
     * Phase states derived from real bracket state: the grand final is the
     * knockout's last round; "playoffs" are every knockout round before it.
     */
    const finalRound = knockoutRounds[knockoutRounds.length - 1];
    const finalDone = Boolean(finalRound?.matches.length && finalRound.matches.every((m) => m.winnerId));
    const finalLive = !finalDone && Boolean(finalRound?.matches.some((m) => m.team1Id && m.team2Id));
    const earlierRounds = knockoutRounds.slice(0, -1);
    /*
     * Vacuously done when there are no earlier rounds (a single-round knockout
     * where group winners meet directly in the grand final) — otherwise the
     * Playoffs step would be pinned to "in progress" for the tournament's life.
     * Still gated on groupStageComplete so it doesn't read "done" before the
     * knockout even exists.
     */
    const playoffsDone = groupStageComplete && earlierRounds.every((r) => r.matches.every((m) => m.winnerId));
    const playoffsLive = groupStageComplete && !playoffsDone;

    const phases: Array<{ name: string, sub: string, state: PhaseState }> = [
        {
            name: "Group Stage",
            sub: `${groups.length} ${groups.length === 1 ? "group" : "groups"}${groupStageComplete ? " · complete" : ""}`,
            state: groupStageComplete ? "done" : "live",
        },
        {
            name: "Playoffs",
            sub: playoffsDone ? "complete" : playoffsLive ? "in progress" : "awaits group stage",
            state: playoffsDone ? "done" : playoffsLive ? "live" : "pending",
        },
        {
            name: "Grand Final",
            sub: finalDone ? championName ? `won by ${championName}` : "complete" : finalLive ? "live" : "TBD",
            state: finalDone ? "done" : finalLive ? "live" : "pending",
        },
    ];

    return (
        <div className="bracket-viewer bracket-viewer--groups">
            <div className="bracket-viewer__phases" role="group" aria-label="Bracket phase">
                {phases.map((phase, i) =>
                    <div key={phase.name} className={`bracket-viewer__phase bracket-viewer__phase--${phase.state}`}>
                        <span className="bracket-viewer__phase-num">{i + 1}</span>
                        <span className="bracket-viewer__phase-body">
                            <span className="bracket-viewer__phase-name">{phase.name}</span>
                            <span className="bracket-viewer__phase-sub">{phase.sub}</span>
                        </span>
                    </div>)}
            </div>

            <div className="bracket-viewer__groups-grid">
                {groups.map((group) => {
                    /*
                     * Display-only re-sort (points, then map differential) so the rows
                     * render top-down even if the payload isn't pre-ordered. This is a
                     * presentational tiebreak, NOT the engine's canonical advancement
                     * chain (points → head-to-head → differential → …) — advancement is
                     * decided backend-side; the row order here never drives it.
                     */
                    const standings = [...group.standings ?? []].sort((a, b) =>
                        b.points - a.points || b.pointsFor - b.pointsAgainst - (a.pointsFor - a.pointsAgainst));
                    return (
                        <section key={group.groupId} className="bracket-viewer__group">
                            <header className="bracket-viewer__group-header">
                                <h4 className="bracket-viewer__group-name">{group.name}</h4>
                                <span className="bracket-viewer__group-advance">Top {group.advancementSlots} advance</span>
                            </header>
                            <table className="bracket-viewer__group-table">
                                <tbody>
                                    {standings.map((s, i) => {
                                        const advancing = i < group.advancementSlots;
                                        const isYou = highlightTeamId && s.participantId === highlightTeamId;
                                        const logoUrl = roundProps.teams[s.participantId]?.logoUrl;
                                        const diff = s.pointsFor - s.pointsAgainst;
                                        return (
                                            <tr
                                                key={s.participantId}
                                                className={[
                                                    advancing ? "bracket-viewer__group-row--advancing" : "",
                                                    isYou ? "bracket-viewer__group-row--you" : "",
                                                ].filter(Boolean).join(" ") || undefined}
                                            >
                                                <td className="bracket-viewer__group-qbar" aria-hidden="true" />
                                                <td className="bracket-viewer__group-rank">{i + 1}</td>
                                                <td className="bracket-viewer__group-team-col">
                                                    <span className="bracket-viewer__standings-cell">
                                                        {logoUrl && <img className="bracket-viewer__standings-logo" src={logoUrl} alt="" />}
                                                        <span className="bracket-viewer__standings-name">{name(s.participantId)}</span>
                                                        {isYou && <span className="bracket-viewer__standings-you">YOU</span>}
                                                    </span>
                                                </td>
                                                <td className="bracket-viewer__standings-wl">{s.wins}–{s.losses}</td>
                                                <td className={diff > 0 ? "bracket-viewer__standings-pos" : diff < 0 ? "bracket-viewer__standings-neg" : undefined}>
                                                    {diff > 0 ? `+${diff}` : diff}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </section>
                    );
                })}
            </div>

            {knockoutRounds.length > 0 &&
                <>
                    <BracketCanvas title={roundProps.compact ? undefined : "Knockout"}>
                        <div className="bracket-viewer__rounds">
                            {knockoutRounds.map((round) =>
                                <BracketRound key={round.roundNumber} round={round} {...roundProps} />)}

                            <div className="bracket-viewer__champion">
                                <div className="bracket-viewer__round-header">
                                    <h4 className="bracket-viewer__round-title">Champion</h4>
                                </div>
                                <div className="bracket-viewer__champion-slot">
                                    <FaTrophy className="bracket-viewer__trophy" />
                                    <span className="bracket-viewer__champion-name">{championName || "TBD"}</span>
                                </div>
                            </div>
                        </div>
                    </BracketCanvas>
                </>
            }
        </div>
    );
}
